import { z } from 'zod'
import {
  themeConfigSchema,
  transcriptionStyleSchema,
  wordSchema,
} from '../types/schemas'
import { FPS } from '../types/video'
import { COMPONENT_PROPS } from './components/componentProps'
import {
  backdropSchema,
  highlightIdSchema,
  mediaSourceSchema,
  themeFieldSchema,
  videoFitSchema,
} from '../types/renderConfigV2'
import { LAYOUT_KEYS } from '../types/layout'

/**
 * The document the timeline composition renders (#77).
 *
 * One hydrated shape for two consumers: the render editor loads it verbatim
 * as its undoable state, and Lambda renders it as `inputProps`. It is the
 * Editor Starter's `UndoableState` — tracks of item ids, an id-keyed item
 * dictionary in FRAMES, an id-keyed asset dictionary with resolved URLs —
 * minus the upload bookkeeping, plus our captions and component items, the
 * theme, and a `zealous` pocket on each item so the save mapper can write the
 * persisted v2 config back without a lookup.
 *
 * Frames, not seconds: the Starter's timeline arithmetic is in frames, and
 * `frames.ts` is the one place seconds become frames. Seconds survive the
 * round trip because unedited items keep `zealous.seconds`.
 */

const int = z.number().int()
const num = z.number()

const baseItem = {
  id: z.string(),
  durationInFrames: int.min(1),
  from: int.min(0),
  top: num,
  left: num,
  width: num,
  height: num,
  opacity: num,
  isDraggingInTimeline: z.boolean(),
}

const zealousSeconds = z.object({ from: num, duration: num }).optional()
/** The persisted `picked` (#547): theme fields the user set by hand. */
const zealousPicked = z.array(themeFieldSchema).optional()

/**
 * The item a follower reads (#541, #566): the id of the scene or free media
 * item it `follows` (captions its words, a speaker text or picture its
 * speaker spans, the visualizer its sound), and on any other item the scene
 * a layout placed it for (persisted `scene`). After `lowerScenes` a scene's
 * id is the item that carries its sound, and a free media item keeps its
 * own, so a follower reads either there.
 */
const zealousSceneId = z.string().optional()

const mediaZealous = z.object({
  source: mediaSourceSchema,
  sceneId: zealousSceneId,
  highlightId: highlightIdSchema.optional(),
  /**
   * A recording scene's window end, in recording time (#77): the item plays
   * `videoStartFromInSeconds`/`audioStartFromInSeconds` … this, minus the
   * transcript's cuts (`timing/sceneMap.ts`). Filled at hydration, from
   * `sourceStart + duration` for a document saved before it existed.
   */
  sourceEnd: num.optional(),
  /** The persisted seconds this item was hydrated from, when unedited. */
  seconds: zealousSeconds,
  picked: zealousPicked,
})

const plainZealous = z
  .object({
    seconds: zealousSeconds,
    picked: zealousPicked,
    sceneId: zealousSceneId,
  })
  .optional()

/**
 * A speaker pin (#566): the participant a speaker element always shows, lit
 * while they speak. Hydrated from the persisted `participantId`. Declared here
 * because `renderChunk` parses the document through this schema before
 * Lambda, and zod strips what the schema does not name.
 */
const zealousParticipantId = z.string().optional()

const componentZealous = z
  .object({
    seconds: zealousSeconds,
    picked: zealousPicked,
    sceneId: zealousSceneId,
    /** A speaker picture's pin; see `zealousParticipantId`. */
    participantId: zealousParticipantId,
  })
  .optional()

const crop = {
  cropLeft: num,
  cropTop: num,
  cropRight: num,
  cropBottom: num,
}

export const videoItemSchema = z.object({
  ...baseItem,
  type: z.literal('video'),
  borderRadius: num,
  rotation: num,
  ...crop,
  videoStartFromInSeconds: num,
  decibelAdjustment: num,
  playbackRate: num,
  audioFadeInDurationInSeconds: num,
  audioFadeOutDurationInSeconds: num,
  fadeInDurationInSeconds: num,
  fadeOutDurationInSeconds: num,
  assetId: z.string(),
  keepAspectRatio: z.boolean(),
  /** Ours (#547): absent is Auto (`timeline/fit.tsx`). */
  fit: videoFitSchema.optional(),
  /** Ours (#547): what fills the box around a fitted video; absent, a blur. */
  backdrop: backdropSchema.optional(),
  zealous: mediaZealous,
})

export const audioItemSchema = z.object({
  ...baseItem,
  type: z.literal('audio'),
  audioStartFromInSeconds: num,
  decibelAdjustment: num,
  playbackRate: num,
  audioFadeInDurationInSeconds: num,
  audioFadeOutDurationInSeconds: num,
  assetId: z.string(),
  zealous: mediaZealous,
})

export const imageItemSchema = z.object({
  ...baseItem,
  type: z.literal('image'),
  borderRadius: num,
  rotation: num,
  ...crop,
  assetId: z.string(),
  keepAspectRatio: z.boolean(),
  fadeInDurationInSeconds: num,
  fadeOutDurationInSeconds: num,
  /**
   * Hydrated only (#547): a template tile's still of a video carries the
   * video's resolved fit and backdrop. Never persisted: the v2 image item
   * has neither, and nothing but `thumbnailDoc` sets them.
   */
  fit: videoFitSchema.optional(),
  backdrop: backdropSchema.optional(),
  zealous: plainZealous,
})

export const fontStyleSchema = z.object({
  variant: z.string(),
  weight: z.string(),
})

export const textItemSchema = z.object({
  ...baseItem,
  type: z.literal('text'),
  rotation: num,
  text: z.string(),
  color: z.string(),
  align: z.enum(['left', 'center', 'right']),
  fontFamily: z.string(),
  fontStyle: fontStyleSchema,
  fontSize: num,
  lineHeight: num,
  letterSpacing: num,
  resizeOnEdit: z.boolean(),
  direction: z.enum(['ltr', 'rtl']),
  strokeWidth: num,
  strokeColor: z.string(),
  /** Ours: the most lines the text shows (#546); absent, it wraps freely. */
  maxLines: num.optional(),
  fadeInDurationInSeconds: num,
  fadeOutDurationInSeconds: num,
  background: z
    .object({
      color: z.string(),
      horizontalPadding: num,
      borderRadius: num,
    })
    .nullable(),
  zealous: z
    .object({
      seconds: zealousSeconds,
      picked: zealousPicked,
      sceneId: zealousSceneId,
      /**
       * Ours: the words are the name or handle of whoever is speaking in
       * `zealous.sceneId`; `text` is empty. `forTrackId` is the previous
       * generation's track binding, read by `useFollowedScenes` only while a
       * Trigger deploy may still send it (#541); the editor never writes it.
       */
      dynamic: z
        .object({
          field: z.enum(['name', 'handle']),
          forTrackId: z.string().optional(),
          /** The pinned participant (#566); absent, whoever speaks. */
          participantId: zealousParticipantId,
        })
        .optional(),
    })
    .optional(),
})

/**
 * Ours (#541, #566): a scene, one shot of the storyboard. It draws nothing:
 * its sound and window are what its views show and its followers read, and
 * `lowerScenes` turns it into the media item that plays them before the
 * layers render. Without a source (`assetId` absent) it is a title card or
 * an outro, a span the elements of its layout belong to.
 */
export const sceneItemSchema = z.object({
  ...baseItem,
  type: z.literal('scene'),
  assetId: z.string().optional(),
  /** Where its window starts, in its source's time (as an audio item's). */
  audioStartFromInSeconds: num.optional(),
  decibelAdjustment: num,
  playbackRate: num,
  audioFadeInDurationInSeconds: num,
  audioFadeOutDurationInSeconds: num,
  zealous: z.object({
    source: mediaSourceSchema.optional(),
    /** As a media item's: the window end, in recording time. */
    sourceEnd: num.optional(),
    highlightId: highlightIdSchema.optional(),
    /** The layout the editor re-applies to the scene's items; the render never reads it. */
    layout: z.enum(LAYOUT_KEYS).optional(),
    seconds: zealousSeconds,
    picked: zealousPicked,
  }),
})

/**
 * Ours (#541, #566): a view, where and how its scene's picture shows. Its
 * span is the scene's; `lowerScenes` makes it a video item playing the
 * scene's window.
 */
export const viewItemSchema = z.object({
  ...baseItem,
  type: z.literal('view'),
  borderRadius: num,
  rotation: num,
  ...crop,
  fadeInDurationInSeconds: num,
  fadeOutDurationInSeconds: num,
  keepAspectRatio: z.boolean(),
  fit: videoFitSchema.optional(),
  backdrop: backdropSchema.optional(),
  zealous: z.object({
    sceneId: z.string(),
    seconds: zealousSeconds,
    picked: zealousPicked,
  }),
})

export const solidItemSchema = z.object({
  ...baseItem,
  type: z.literal('solid'),
  borderRadius: num,
  rotation: num,
  color: z.string(),
  /** Ours: a border inside the box (#546), in composition pixels. */
  stroke: z.object({ color: z.string(), width: num }).optional(),
  keepAspectRatio: z.boolean(),
  fadeInDurationInSeconds: num,
  fadeOutDurationInSeconds: num,
  zealous: plainZealous,
})

/**
 * Ours: captions show the words of the item in `zealous.sceneId` (a scene or
 * a free recording item); their timing is derived from it. `forTrackId` is the previous generation's track
 * binding, accepted while an older Trigger may still send it (#541).
 */
export const captionsItemSchema = z.object({
  ...baseItem,
  type: z.literal('captions'),
  rotation: num,
  forTrackId: z.string().optional(),
  style: transcriptionStyleSchema,
  zealous: plainZealous,
})

function componentArm<K extends keyof typeof COMPONENT_PROPS>(componentId: K) {
  return z.object({
    ...baseItem,
    type: z.literal('component'),
    rotation: num,
    componentId: z.literal(componentId),
    props: COMPONENT_PROPS[componentId],
    /** The previous generation's track binding (#541); see captions. */
    forTrackId: z.string().optional(),
    fadeInDurationInSeconds: num,
    fadeOutDurationInSeconds: num,
    zealous: componentZealous,
  })
}

/** Ours: a registry component with validated props. */
export const componentItemSchema = z.discriminatedUnion('componentId', [
  componentArm('title-card'),
  componentArm('logo-outro'),
  componentArm('audio-visualizer'),
  componentArm('picture'),
])

export const timelineItemSchema = z.discriminatedUnion('type', [
  videoItemSchema,
  audioItemSchema,
  sceneItemSchema,
  viewItemSchema,
  imageItemSchema,
  textItemSchema,
  solidItemSchema,
  captionsItemSchema,
  componentItemSchema,
])

export type VideoItem = z.infer<typeof videoItemSchema>
export type SceneItem = z.infer<typeof sceneItemSchema>
export type ViewItem = z.infer<typeof viewItemSchema>
export type AudioItem = z.infer<typeof audioItemSchema>
export type ImageItem = z.infer<typeof imageItemSchema>
export type TextItem = z.infer<typeof textItemSchema>
export type SolidItem = z.infer<typeof solidItemSchema>
export type CaptionsItem = z.infer<typeof captionsItemSchema>
export type ComponentItem = z.infer<typeof componentItemSchema>
export type TimelineItem = z.infer<typeof timelineItemSchema>
export type FontStyle = z.infer<typeof fontStyleSchema>
export type TextAlign = TextItem['align']
export type TextDirection = TextItem['direction']
export type TextItemBackground = NonNullable<TextItem['background']>

const baseAsset = {
  id: z.string(),
  filename: z.string(),
  size: num,
  remoteUrl: z.string().nullable(),
  remoteFileKey: z.string().nullable(),
  mimeType: z.string(),
}

export const videoAssetSchema = z.object({
  ...baseAsset,
  type: z.literal('video'),
  durationInSeconds: num,
  hasAudioTrack: z.boolean(),
  width: num,
  height: num,
  /** The audio-only rendition for analysis, when one is recorded. */
  audioUrl: z.string().optional(),
  /** `waveform.dat`, for the visualizer's whole-file peak. */
  waveformUrl: z.string().optional(),
  /**
   * The Mux playback id, when the asset is on Mux (#546): a still of any
   * second of it is `image.mux.com/<id>/thumbnail.webp?time=<s>`, which is
   * what a template tile draws instead of decoding the video.
   */
  playbackId: z.string().optional(),
  /** The recorded poster, when there is one; the tile's second choice. */
  thumbnailUrl: z.string().optional(),
})

export const audioAssetSchema = z.object({
  ...baseAsset,
  type: z.literal('audio'),
  durationInSeconds: num,
  audioUrl: z.string().optional(),
  waveformUrl: z.string().optional(),
})

export const imageAssetSchema = z.object({
  ...baseAsset,
  type: z.literal('image'),
  width: num,
  height: num,
})

/** Who spoke when, in recording time, as the speaker element reads it. */
export const speakerSpanSchema = z.object({
  start: num,
  end: num,
  name: z.string().nullable(),
  handle: z.string().nullable(),
  avatarUrl: z.string().nullable().optional(),
  /**
   * The participant who spoke (#566): what a pinned slot matches on. Absent
   * on a document hydrated before pins existed.
   */
  participantId: z.string().optional(),
})
export type SpeakerSpan = z.infer<typeof speakerSpanSchema>

/**
 * One of a recording's named participants (#566): what a pinned slot shows
 * whether or not they speak in the window. Named and pictured by the same
 * precedence as the spans; a participant with neither name nor handle is
 * left out, as their spans are.
 */
export const transcriptParticipantSchema = z.object({
  id: z.string(),
  name: z.string().nullable(),
  handle: z.string().nullable(),
  avatarUrl: z.string().nullable(),
})
export type TranscriptParticipant = z.infer<typeof transcriptParticipantSchema>

/**
 * Ours: a recording's words in recording time, `removed` already dropped,
 * its speaker spans (absent on a document hydrated before they existed), and
 * its cuts: the removed words and `skipSegments` as merged recording-time
 * ranges (`skipRangesOf`), which every scene of the recording plays around
 * (#77). `cutsOnly` marks an asset hydrated for a recording nothing reads
 * words from: its `words` and `speakers` were not loaded.
 */
export const transcriptAssetSchema = z.object({
  id: z.string(),
  type: z.literal('transcript'),
  recordingId: z.string(),
  words: z.array(wordSchema),
  speakers: z.array(speakerSpanSchema).optional(),
  /**
   * The recording's named participants, from its rows (#566): never pruned
   * to the windows, and present while a new transcript is in flight, so a
   * pinned label keeps its name and face. Absent on an older document.
   */
  participants: z.array(transcriptParticipantSchema).optional(),
  skips: z.array(z.object({ start: num, end: num })).optional(),
  cutsOnly: z.boolean().optional(),
})

export const timelineAssetSchema = z.discriminatedUnion('type', [
  videoAssetSchema,
  audioAssetSchema,
  imageAssetSchema,
  transcriptAssetSchema,
])

export type VideoAsset = z.infer<typeof videoAssetSchema>
export type AudioAsset = z.infer<typeof audioAssetSchema>
export type ImageAsset = z.infer<typeof imageAssetSchema>
export type TranscriptAsset = z.infer<typeof transcriptAssetSchema>
export type TimelineAsset = z.infer<typeof timelineAssetSchema>
export type MediaAsset = VideoAsset | AudioAsset | ImageAsset

export const trackSchema = z.object({
  id: z.string(),
  items: z.array(z.string()),
  hidden: z.boolean(),
  muted: z.boolean(),
})
export type TrackType = z.infer<typeof trackSchema>

export const timelineDocSchema = z.object({
  fps: z.literal(FPS),
  compositionWidth: int.positive(),
  compositionHeight: int.positive(),
  theme: themeConfigSchema,
  tracks: z.array(trackSchema),
  items: z.record(z.string(), timelineItemSchema),
  assets: z.record(z.string(), timelineAssetSchema),
  /** The template last applied, carried verbatim from the config. */
  template: z.object({ layout: z.enum(LAYOUT_KEYS) }).optional(),
  /**
   * Whether the Zealous mark is drawn over the render (#546): decided at
   * hydration from the org's plan (on for the free plan), never stored in
   * the config, never an item. Optional, so an older `z-r` bundle that
   * strips the key degrades to "no mark", never to a crash.
   */
  watermark: z.boolean().optional(),
})
export type TimelineDoc = z.infer<typeof timelineDocSchema>

/** The transcript asset id for a recording. */
export function transcriptAssetId(recordingId: string): string {
  return `transcript:${recordingId}`
}

export function isMediaAsset(asset: TimelineAsset): asset is MediaAsset {
  return asset.type !== 'transcript'
}
